import React, { useMemo } from "react";
import styled from "styled-components";
import moment from "moment";

import { TagVariant } from "../../../../design-system";
import {
  OutreachRail,
  OutreachRailSnapshot,
} from "../../../../types/OutreachMetrics";

// The shared rail vocabulary, and the capacity trend chart.
//
// This file used to be a whole panel: a stat grid, a status pill row, one bar
// per rail, and the trend. The first three said what the rail table already
// says, from the same snapshot document, so they went. The trend is the one
// thing the table cannot show, and it now opens from the table it explains.
//
// The chart is hand authored inline SVG on the design system's theme tokens, so
// it follows light and dark with the rest of the dashboard.

const TREND_DAYS = 14;

// Channel swatches reuse the semantic theme tokens rather than introducing a
// second palette into the dashboard.
//
// These four maps are exported because the Analysis tab renders the same rails
// in a table. One vocabulary, one palette: a status pill must not read
// "warning" here and "danger" there.
export const CHANNEL_COLOR: Record<string, string> = {
  li: "var(--color-info)",
  ig: "var(--color-accent)",
  em: "var(--color-warning)",
  x: "var(--color-text-secondary)",
  inmail: "var(--color-success)",
};

export const CHANNEL_LABEL: Record<string, string> = {
  li: "LinkedIn",
  ig: "Instagram",
  em: "Email",
  x: "X",
  inmail: "InMail",
};

export const STATUS_VARIANT: Record<string, TagVariant> = {
  ok: "success",
  under: "warning",
  silent: "warning",
  disarmed: "neutral",
  dead: "danger",
  blocked: "danger",
  manual: "neutral",
};

// Plain language for each status, so the pills do not need a legend.
export const STATUS_HINT: Record<string, string> = {
  ok: "delivering",
  under: "pool ran short",
  silent: "healthy but sent nothing",
  disarmed: "built, not armed",
  dead: "worker or session error",
  blocked: "platform block",
  manual: "sent by hand",
};

const Swatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex: none;
  background: ${(p) => p.$color};
`;

const Legend = styled.div`
  display: flex;
  gap: var(--space-4);
  margin-top: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
`;

const TrendSvg = styled.svg`
  display: block;
  width: 100%;
  height: auto;
  margin-top: var(--space-3);
`;

// ─── The 14 day allowed vs delivered trend ───────────────────────────────────

type TrendPoint = { index: number; allowed: number; delivered: number };

const W = 720;
const H = 170;
const PAD_L = 34;
const PAD_R = 8;
const PAD_T = 12;
const PAD_B = 22;

// Consecutive present days only. A day with no snapshot breaks the line into a
// new run, so a routine that did not report reads as a gap and never as a zero.
const runsOf = (points: TrendPoint[]): TrendPoint[][] => {
  const runs: TrendPoint[][] = [];
  let current: TrendPoint[] = [];
  for (const point of points) {
    const previous = current[current.length - 1];
    if (previous && point.index !== previous.index + 1) {
      runs.push(current);
      current = [];
    }
    current.push(point);
  }
  if (current.length) runs.push(current);
  return runs;
};

export const Trend: React.FC<{ snapshots: OutreachRailSnapshot[] }> = ({
  snapshots,
}) => {
  const { points, days, max, labels } = useMemo(() => {
    const sorted = [...snapshots].sort((a, b) => a.date.localeCompare(b.date));
    const last = sorted[sorted.length - 1];
    const end = moment.utc(last.date, "YYYY-MM-DD");
    const start = end.clone().subtract(TREND_DAYS - 1, "days");
    const span = TREND_DAYS;

    const inWindow = sorted.filter(
      (s) => moment.utc(s.date, "YYYY-MM-DD").diff(start, "days") >= 0
    );
    const pts: TrendPoint[] = inWindow.map((s) => ({
      index: moment.utc(s.date, "YYYY-MM-DD").diff(start, "days"),
      allowed: s.totals?.allowed ?? 0,
      delivered: s.totals?.delivered ?? 0,
    }));
    const peak = pts.reduce((m, p) => Math.max(m, p.allowed, p.delivered), 0);
    return {
      points: pts,
      days: span,
      max: peak > 0 ? peak : 1,
      labels: {
        start: start.format("MMM D"),
        end: end.format("MMM D"),
      },
    };
  }, [snapshots]);

  const x = (index: number) =>
    PAD_L +
    (days > 1 ? (index / (days - 1)) * (W - PAD_L - PAD_R) : (W - PAD_L - PAD_R) / 2);
  const y = (value: number) => PAD_T + (1 - value / max) * (H - PAD_T - PAD_B);

  const line = (run: TrendPoint[], pick: (p: TrendPoint) => number) =>
    run.map((p) => `${x(p.index)},${y(pick(p))}`).join(" ");

  // The shaded band is the unused capacity: allowed on top, delivered back
  // along the bottom, closed per run so gaps stay empty.
  const band = (run: TrendPoint[]) =>
    [
      ...run.map((p) => `${x(p.index)},${y(p.allowed)}`),
      ...[...run].reverse().map((p) => `${x(p.index)},${y(p.delivered)}`),
    ].join(" ");

  const runs = runsOf(points);

  return (
    <>
      <TrendSvg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Allowed against delivered over the last 14 days">
        {[0, 0.5, 1].map((t) => {
          const value = Math.round(max * (1 - t));
          const gy = PAD_T + t * (H - PAD_T - PAD_B);
          return (
            <g key={t}>
              <line
                x1={PAD_L}
                y1={gy}
                x2={W - PAD_R}
                y2={gy}
                stroke="var(--color-border)"
                strokeWidth="1"
              />
              <text
                x={PAD_L - 6}
                y={gy + 4}
                textAnchor="end"
                fontSize="10"
                fill="var(--color-text-tertiary)"
              >
                {value}
              </text>
            </g>
          );
        })}

        {runs.map((run, i) => (
          <g key={i}>
            {run.length > 1 ? (
              <>
                <polygon
                  points={band(run)}
                  fill="var(--color-accent)"
                  opacity="0.12"
                />
                <polyline
                  points={line(run, (p) => p.allowed)}
                  fill="none"
                  stroke="var(--color-text-tertiary)"
                  strokeWidth="2"
                  strokeDasharray="4 3"
                />
                <polyline
                  points={line(run, (p) => p.delivered)}
                  fill="none"
                  stroke="var(--color-accent)"
                  strokeWidth="2"
                />
              </>
            ) : (
              // A one-point run has no line to draw: a polyline with a single
              // point renders no stroke at all. Draw a short tick for the
              // allowance and let the delivered dot below stand for the day, so
              // the very first snapshot is visible instead of blank.
              run.map((p) => (
                <g key={`tick-${p.index}`}>
                  <line
                    x1={x(p.index) - 6}
                    y1={y(p.allowed)}
                    x2={x(p.index) + 6}
                    y2={y(p.allowed)}
                    stroke="var(--color-text-tertiary)"
                    strokeWidth="2"
                  />
                  <line
                    x1={x(p.index)}
                    y1={y(p.allowed)}
                    x2={x(p.index)}
                    y2={y(p.delivered)}
                    stroke="var(--color-accent)"
                    strokeWidth="2"
                    opacity="0.35"
                  />
                </g>
              ))
            )}
            {run.map((p) => (
              <circle
                key={p.index}
                cx={x(p.index)}
                cy={y(p.delivered)}
                r="3"
                fill="var(--color-accent)"
              />
            ))}
          </g>
        ))}

        <text x={PAD_L} y={H - 6} fontSize="10" fill="var(--color-text-tertiary)">
          {labels.start}
        </text>
        <text
          x={W - PAD_R}
          y={H - 6}
          textAnchor="end"
          fontSize="10"
          fill="var(--color-text-tertiary)"
        >
          {labels.end}
        </text>
      </TrendSvg>
      <Legend>
        <LegendItem>
          <Swatch $color="var(--color-accent)" /> Delivered
        </LegendItem>
        <LegendItem>
          <Swatch $color="var(--color-text-tertiary)" /> Allowed
        </LegendItem>
        <LegendItem>Days with no run are left blank.</LegendItem>
      </Legend>
    </>
  );
};
